import { motion } from "framer-motion";
import { AlertTriangle, Check, Sparkles, Zap } from "lucide-react";
import GradientBar from "@/components/common/GradientBar";

/**
 * DecisionSummaryCard — renders a live (or last-completed) Decision.
 * Empty state: invites the user to run their first debate.
 */
export default function DecisionSummaryCard({ decision = null, isLive = false }) {
  if (!decision) {
    return (
      <motion.section
        data-testid="decision-summary-card"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
        className="relative flex h-full flex-col items-start justify-between overflow-hidden rounded-[28px] border border-dashed border-black/[0.08] bg-white/70 p-6 backdrop-blur"
      >
        <div className="eyebrow">Decision Summary</div>
        <div className="my-auto flex flex-col items-start gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-zinc-900 to-black">
            <Sparkles className="h-4 w-4 text-white" strokeWidth={2.4} />
          </span>
          <h2
            data-testid="decision-summary-empty"
            className="text-[20px] font-extrabold leading-tight tracking-tight text-zinc-900"
          >
            Run your first debate.
          </h2>
          <p className="text-[13px] leading-relaxed text-zinc-500">
            Drop a goal below. Eight executives will weigh in. We'll synthesise
            their debate into one decision with confidence, strategy, and risks.
          </p>
        </div>
        <div className="mt-auto pt-6 text-[11px] text-zinc-400">
          Live decisions appear here once the boardroom finishes.
        </div>
      </motion.section>
    );
  }

  return (
    <motion.section
      data-testid="decision-summary-card"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
      className="relative flex h-full flex-col overflow-hidden rounded-[28px] border border-black/[0.04] bg-white/85 p-6 backdrop-blur"
      style={{
        boxShadow:
          "inset 0 1px 0 rgba(255,255,255,0.9), 0 24px 48px -28px rgba(15,15,25,0.18), 0 1px 0 rgba(0,0,0,0.02)",
      }}
    >
      <div className="flex items-start justify-between">
        <div className="eyebrow">Decision Summary</div>
        {isLive && (
          <span
            data-testid="decision-summary-live"
            className="inline-flex items-center gap-1.5 rounded-full bg-black px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white"
          >
            <Zap className="h-3 w-3" strokeWidth={2.6} />
            Live
          </span>
        )}
      </div>

      <h2
        data-testid="decision-summary-title"
        className="mt-4 text-[22px] font-extrabold leading-tight tracking-tight text-zinc-900"
      >
        {decision.title}
      </h2>

      <div className="mt-6">
        <div className="flex items-baseline justify-between">
          <div className="eyebrow">Confidence</div>
          <div
            data-testid="decision-summary-confidence"
            className="text-[42px] font-extrabold leading-none tracking-tight text-zinc-900"
          >
            {decision.confidence}
            <span className="ml-0.5 text-[18px] font-bold text-zinc-400">%</span>
          </div>
        </div>
        <div className="mt-3">
          <GradientBar value={decision.confidence} testId="confidence-bar" height={8} />
        </div>
      </div>

      <div className="mt-6">
        <div className="eyebrow mb-2.5">Strategy</div>
        <ul className="space-y-2.5">
          {(decision.strategy || []).map((s, i) => (
            <li
              key={i}
              data-testid={`strategy-item-${i}`}
              className="flex items-start gap-2.5 text-[13px] leading-snug text-zinc-700"
            >
              <span className="mt-[3px] inline-flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-full bg-zinc-900 text-white">
                <Check className="h-2.5 w-2.5" strokeWidth={3} />
              </span>
              <span>{s}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-6">
        <div className="eyebrow mb-2.5">Risks</div>
        <ul className="space-y-2.5">
          {(decision.risks || []).map((r, i) => (
            <li
              key={i}
              data-testid={`risk-item-${i}`}
              className="flex items-start gap-2.5 text-[13px] leading-snug text-zinc-500"
            >
              <span className="mt-[3px] inline-flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-full bg-zinc-200 text-zinc-700">
                <AlertTriangle className="h-2.5 w-2.5" strokeWidth={2.6} />
              </span>
              <span>{r}</span>
            </li>
          ))}
        </ul>
      </div>

      {decision.next_steps && decision.next_steps.length > 0 && (
        <div className="mt-6">
          <div className="eyebrow mb-2.5">Next Steps</div>
          <ul className="space-y-2.5">
            {decision.next_steps.slice(0, 6).map((n, i) => (
              <li
                key={i}
                data-testid={`next-step-${i}`}
                className="flex items-start gap-2.5 text-[13px] leading-snug text-zinc-700"
              >
                <span className="mt-[2px] flex-shrink-0 text-zinc-400">→</span>
                <span>{n}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-auto pt-6 text-[11px] text-zinc-400">
        Synthesized live by Claude Sonnet 4 from the boardroom debate.
      </div>
    </motion.section>
  );
}
